import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
// import { StringNullableChain } from 'cypress/types/lodash';
import { NgToastTranslocoService } from 'src/app/common/services/ng-toast-transloco/ng-toast-transloco.service';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { CountryStatesDataService } from 'src/app/shared/services/country-states-data.service';
import { PlayerRegistrationErrorParser } from '../../error-parsers/player_registration_error_parser';
import { LoginDetailsService } from '../../services/login-details.service';
import { LoginService } from '../../services/login.service';
import countriesData from 'src/resources/countries.json';

@Component({
  selector: 'ds-new-player-registration',
  // standalone: true,
  // imports: [],
  templateUrl: './new-player-registration.component.html',
  styleUrl: './new-player-registration.component.scss'
})
export class NewPlayerRegistrationComponent implements OnInit, AfterViewInit {

  newPlayerRegistrationForm: FormGroup;

  @Input() appPlayerManagementSystemAttributes: any;
  @Input() userName: any;
  @Input() email: any;
  @Input() addressType: string;
  @Input() phoneType: string;
  @Input() emailType: string;
  @Input() identTypeArray: any;
  @Input() termsUrl: any;
  @Output() goToSignin = new EventEmitter();
  @Output() onLogout = new EventEmitter();

  showPassword = false;
  maxDate: Date;
  maxIssuedDate: Date;
  minExpiryDate: Date;
  dateTimeFormat: string;
  datePickerPlaceholder: string;
  countryAndStatesData: { key: string; name: any; states: { key: string; name: any }[] | null; }[];
  selectedCountry: { key: string; name: any; states: { key: string; name: any }[] | null; };
  selectedState: { key: string; name: any } | null;
  identTypes: { key: string; value: any; }[];
  countries = countriesData;
  hidePlayerPin: boolean = false;
  playerMinimumAgeLimit: number = 21;

  constructor(private cdr: ChangeDetectorRef, private loginService: LoginService, private loginDetailsService: LoginDetailsService,
    private toast: NgToastTranslocoService, private countryAndStatesDataService: CountryStatesDataService,
    private router: Router, private authService: AuthService,
    private dateAdapter: DateAdapter<any>,
    @Inject(MAT_DATE_FORMATS) private dateFormats: any) {

    this.setMaxDate();
    this.setMaxIssuedDate();
    this.setMinExpiryDate();
    this.dateTimeFormat = this.loginDetailsService.supportedDateTimeFormat || 'MMM DD, YYYY';
    const momentFormat = this.configureDateFormat();
    this.datePickerPlaceholder = momentFormat.toLowerCase();
    this.hidePlayerPin = this.loginDetailsService.playerRegistrationOptionalFields.PlayerPIN.Hide;
    this.playerMinimumAgeLimit = this.loginDetailsService.playerMinimumAgeLimit;
  }

  ngOnInit(): void {
    this.newPlayerRegistrationForm = new FormGroup({
      firstName: new FormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(50), Validators.pattern(/^[a-zA-Z ]+$/)]),
      lastName: new FormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(50), Validators.pattern(/^[a-zA-Z ]+$/)]),
      dob: new FormControl('', [Validators.required]),
      gender: new FormControl('Male', [Validators.required]),
      phoneCountryCode: new FormControl('', [Validators.required]),
      mobileNumber: new FormControl('', [
        Validators.required,
        Validators.pattern(/^[0-9]+$/),
        Validators.minLength(10),
        Validators.maxLength(10)
      ]),
      playerPin: new FormControl(''),
      addressLine1: new FormControl('', [Validators.required, Validators.maxLength(100)]),
      addressLine2: new FormControl('', [Validators.maxLength(100)]),
      city: new FormControl('', [Validators.required]),
      state: new FormControl(this.selectedState, [Validators.required]),
      country: new FormControl(this.selectedCountry, [Validators.required]),
      zip: new FormControl('', [Validators.required]),
      identityType: new FormControl('', [Validators.required]),
      identityNumber: new FormControl('', [Validators.required, Validators.maxLength(50)]),
      issuedState: new FormControl('', [Validators.required]),
      issuedDate: new FormControl('', [Validators.required]),
      expiryDate: new FormControl('', [Validators.required]),
      acceptTerms: new FormControl(false, [Validators.requiredTrue])  // For the terms acceptance
    });
    this.updatePlayerPinValidator();
  }

  updatePlayerPinValidator() {
    const control = this.newPlayerRegistrationForm.get('playerPin');
    if (!this.hidePlayerPin) {
      control?.setValidators([Validators.required, Validators.pattern(/^\d{4}$/)]);
    } else {
      control?.clearValidators();
    }
    control?.updateValueAndValidity();
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
    this.getCountryAndStatesData();
    this.initIdentTypes();

  }

  configureDateFormat(): string {
    // Update the date formats based on config
    // Convert Angular format to Moment format
    let inputFormat = this.dateTimeFormat;

    // Detect if format uses month name (MMM) - case insensitive
    const hasMonthName = /MMM/i.test(inputFormat);

    let momentFormat: string;

    if (hasMonthName) {
      if (inputFormat.includes('dd')) {
        // For month name formats: MMM dd,yyyy → MMM DD YYYY
        momentFormat = inputFormat
          .replace(/MMM/gi, 'MMM')    // Normalize to uppercase MMM
          .replace(/dd/gi, 'DD')      // day
          .replace(/yyyy/gi, 'YYYY')  // year
          .replace(/[,\/]/g, ' ')     // replace commas and slashes with spaces
          .replace(/\s+/g, ' ')       // normalize multiple spaces
          .trim();
      } else {
        // For month name formats: MMM d,yyyy → MMM D YYYY
        momentFormat = inputFormat
          .replace(/MMM/gi, 'MMM')    // Normalize to uppercase MMM
          .replace(/d/gi, 'D')        // day
          .replace(/yyyy/gi, 'YYYY')  // year
          .replace(/[,\/]/g, ' ')     // replace commas and slashes with spaces
          .replace(/\s+/g, ' ')       // normalize multiple spaces
          .trim();
      }

    } else {
      // For numeric formats: mm/dd/yyyy or dd,mm,yyyy → MM DD YYYY
      momentFormat = inputFormat
        .replace(/yyyy/gi, 'YYYY')  // year
        .replace(/dd/gi, 'DD')      // day
        .replace(/mm/gi, 'MM')      // month numeric
        .replace(/[,\/]/g, ' ')     // replace commas and slashes with spaces
        .replace(/\s+/g, ' ')       // normalize multiple spaces
        .trim();
    }

    this.dateFormats.display.dateInput = momentFormat;
    this.dateFormats.parse.dateInput = [momentFormat, 'MM/DD/YYYY', 'MMM DD YYYY', 'DD MM YYYY', 'DD/MM/YYYY'];

    return momentFormat;
  }

  private setDefaultStateAndCountryCode() {
    const defaultCountryCode = this.loginDetailsService.defaultPlayerLocation?.CountryCode ?? 'US';
    const defaultStateCode = this.loginDetailsService.defaultPlayerLocation?.StateCode ?? 'NV';

    const country = this.countryAndStatesData.find(country => country.key === defaultCountryCode);

    if (country) {
      this.selectedCountry = { key: defaultCountryCode, name: country.name, states: country.states };
      this.selectedState = defaultStateCode;
    }

    if (this.selectedCountry) {
      const defaultDialCode = this.countries.find(c => c.code === defaultCountryCode)?.dial_code ?? '+1';
      // Set the default values 
      this.newPlayerRegistrationForm.patchValue({
        country: this.selectedCountry.key,
        state: this.selectedState,
        issuedState: this.selectedState,
        phoneCountryCode: defaultDialCode
      });
    }

  }

  goToSignIn() {
    this.goToSignin.emit();
  }

  get loginEmailAddress() {
    return this.userName.value || this.email || localStorage.getItem('EMAIL') || '';
  }

  get maskedLoginEmailAddress(): string {
    let email = this.userName.value || this.email;
    if (email?.includes('@')) {
      return this.maskEmail(email);
    }
    email = localStorage.getItem('EMAIL');
    return this.maskEmail(email);
  }

  private maskEmail(username: string): string {
    if (!username || username.trim() === '' || !username.includes('@')) {
      return username;
    }

    try {
      const atIndex = username.indexOf('@');
      if (atIndex <= 2) {
        return username; // Not enough characters to mask
      }

      const maskedPart = '*'.repeat(atIndex - 2);
      const result = username.substring(0, 1) + maskedPart + username.substring(atIndex - 1);
      return result;
    } catch (e) {
      console.error(e);
      return username;
    }
  }

  setMaxDate() {
    const today = new Date();
    this.playerMinimumAgeLimit = this.loginDetailsService.playerMinimumAgeLimit;
    this.maxDate = new Date(today.getFullYear() - this.playerMinimumAgeLimit, today.getMonth(), today.getDate());
  }
  setMaxIssuedDate() {
    const today = new Date();
    // Set to today's date
    this.maxIssuedDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }
  setMinExpiryDate() {
    const today = new Date();
    // Set to tomorrow's date
    this.minExpiryDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  }

  onCountryChange(selectedCountryKey: string) {
    // Update the selected country and reset the state
    this.selectedCountry = this.countryAndStatesData.find(country => country.key === selectedCountryKey);
    // this.selectedState = null;  // Reset state when country is changed
    this.selectedState = this.selectedCountry?.states?.length > 0 ? this.selectedCountry?.states[0] : null;

    if (this.selectedState && this.selectedState.key) {
      this.newPlayerRegistrationForm.patchValue({
        state: this.selectedState.key,
        issuedState: this.selectedState.key
      })
    }
  }

  onStateChange(selectedState: string) {
    this.newPlayerRegistrationForm.patchValue({
      issuedState: selectedState
    })
  }

  initIdentTypes() {

    if (!this.identTypeArray)
      return;

    if (!Array.isArray(this.identTypeArray))
      return;

    this.identTypes = this.identTypeArray.map(item => {
      const [key, value] = Object.entries(item)[0];
      return { key, value };
    });

    if (this.identTypes && this.identTypes.length > 0) {
      this.newPlayerRegistrationForm.get('identityType')?.setValue(this.identTypes[0].value);
    }
  }

  async executeNewPlayerRegistration(): Promise<void> {

    if (this.newPlayerRegistrationForm.valid) {
      const payload = { ...this.newPlayerRegistrationForm.value };
      const formatDate = (date: any): string => {
        if (!date) return ''; // Handle null/undefined
        const d = new Date(date);
        return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
      };

      // Format required date fields
      const formattedDob = formatDate(payload.dob);
      const formattedExpiryDate = formatDate(payload.expiryDate);
      const formattedIssuedDate = formatDate(payload.issuedDate);

      // Construct the JSON payload
      const jsonPayload = {
        metadata: {
          propertyId: this.appPlayerManagementSystemAttributes.PropertyId,
          clientId: this.appPlayerManagementSystemAttributes.ClientId,
          transDateTime: payload.transDateTime,
          transStation: payload.transStation,
          transEmployee: this.appPlayerManagementSystemAttributes.TransEmployee,
          transApproved: payload.transApproved,
          transEmployeeRole: this.appPlayerManagementSystemAttributes.TransEmployeeRole,
          transApproverRole: payload.transApproverRole,
          transLocation: this.appPlayerManagementSystemAttributes.TransLocation,
        },
        data: {
          templateAccount: payload.templateAccount || '',
          playerProfile: {
            firstName: payload.firstName,
            lastName: payload.lastName,
            middleInitial: payload.middleInitial,
            preferredName: payload.preferredName,
            generation: payload.generation,
            genderType: payload.gender,
            maritalState: payload.maritalState,
            birthDate: formattedDob,
            anniversaryDate: payload.anniversaryDate,
            playerProperty: payload.playerProperty,
            playerHeight: payload.playerHeight,
            playerWeight: payload.playerWeight,
            hairColor: payload.hairColor,
            eyeColor: payload.eyeColor,
            playerEthnicity: payload.playerEthnicity,
            playerOccupation: payload.playerOccupation,
            playerNationality: payload.playerNationality,
            localeCode: payload.localeCode,
            playerVIP: payload.playerVIP,
            playerWhale: payload.playerWhale,
            playerPrivacy: false,
            playerAltAcct: payload.playerAltAcct,
            altAccessCodeType: payload.altAccessCodeType,
          },

          playerAddress: [
            {
              addressType: this.addressType,
              addressLine1: payload.addressLine1,
              addressLine2: payload.addressLine2,
              addressLine3: payload.addressLine3,
              city: payload.city,
              stateProvCode: payload.state,
              countryCode: payload.country,
              postCode: payload.zip,
              primaryAddress: true,
              mailToAddress: false,
            },
          ],

          playerPhone: [
            {
              phoneType: this.phoneType,
              phoneNumber: `${payload.mobileNumber}`,
              callToPhone: true,
              primaryPhone: payload.primaryPhone,
              sendTextMessage: payload.sendTextMessage,
              phoneExt: `${payload.phoneCountryCode}` || ``,
            },
          ],

          playerEmail: [
            {
              emailType: this.emailType,
              emailAddress: this.userName.value || this.email || localStorage.getItem('EMAIL') || '',
              sendToEmail: true,
              primaryEmail: false,
            },
          ],

          playerIdent: [
            {
              identType: payload.identityType,
              identNumber: payload.identityNumber,
              identExpiry: formattedExpiryDate,
              identIssueDate: formattedIssuedDate,
              identStateCode: payload.issuedState,
              identVerificationMode: payload.identVerificationMode,
            },
          ],
          ...(this.hidePlayerPin
            ? {}
            : {
              playerPin: {
                pinNumber: payload.playerPin,
              },
            }),
        },
      };

      try {
        // Submit the form
        const response: any = await this.loginService.submitForm(jsonPayload);
        const givenName = response.playerId;
        const message = response.message;
        this.loginDetailsService.setPlayerId(givenName);
        this.toast.toastSuccess(`${message} Player ID: ${givenName}`)
        this.authService.navigateToDashboard();
        // Navigate after success
      } catch (ex) {
        console.error('Error submitting form:', ex);

        const error = PlayerRegistrationErrorParser.parse(ex);
        this.toast.toastError(error);
      }
    } else {
      this.toast.toastError('MODULES.LOGIN.REG_ENTER_ALL_FIELDS_ERROR');
    }
  }

  logOut() {

    this.onLogout.emit();
  }

  async getCountryAndStatesData() {
    try {
      const countryResponse = await this.countryAndStatesDataService.fetchCountryAndStatesData();

      if (!countryResponse || typeof countryResponse !== 'object') {
        throw new Error('Invalid API response');
      }

      this.countryAndStatesData = Object.entries(countryResponse).map((responseItem) => {
        if (!responseItem[1] || typeof responseItem[1] !== 'object') {
          console.warn(`Skipping invalid country data: ${responseItem[0]}`, responseItem[1]);
          return null;
        }

        let countryObj = {
          key: responseItem[0],
          name: responseItem[1]['name'] ?? '',
          states: []
        };

        let divisions = responseItem[1]['divisions'];
        if (!divisions || typeof divisions !== 'object') {
          console.warn(`Skipping invalid state data for country: ${responseItem[0]}`);
          return countryObj;
        }

        countryObj.states = Object.keys(divisions).map((stateItemKey) => ({
          key: stateItemKey,
          name: divisions[stateItemKey] ?? '',
        }));

        return countryObj;
      }).filter(Boolean); // Removes null entries

      this.setDefaultStateAndCountryCode();

    } catch (error) {
      console.error('Error fetching country and state data:', error);
    }
  }
}
